import {
  Control,
  Controller,
  FieldErrors,
  UseFormSetValue,
  UseFormTrigger,
  UseFormWatch,
} from 'react-hook-form';
import { Dispatch, SetStateAction, useState } from 'react';
import { PolicyAction, PolicyFormValues } from '../policy-dialog';
import { emailRegex } from '../../../utils/regular-expressions';
import UsersAutocomplete from '../../../shared/users-autocomplete';

import z from 'zod';

import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Delete from '@mui/icons-material/Delete';

interface RemoveSpecificSharesActionProps {
  setValue: UseFormSetValue<PolicyFormValues>;
  control: Control<PolicyFormValues>;
  watch: UseFormWatch<PolicyFormValues>;
  errors: FieldErrors<PolicyFormValues>;
  trigger: UseFormTrigger<PolicyFormValues>;
}

export const RemoveSpecificSharesActionSchema = z.object({
  type: z.literal('Remove_Specific_Shares'),
  externalshare: z.array(z.string()).min(1, 'A share address is required.'),
});

export type RemoveSpecificSharesActionType = z.infer<
  typeof RemoveSpecificSharesActionSchema
>;

// Type guard to check if an action is RemoveSpecificSharesActionType
function isRemoveSpecificSharesAction(
  action: PolicyAction
): action is RemoveSpecificSharesActionType {
  return action.type === 'Remove_Specific_Shares';
}

export default function RemoveSpecificSharesAction({
  setValue,
  watch,
  control,
  errors,
  trigger,
}: RemoveSpecificSharesActionProps) {
  const [shareAddress, setShareAddress] = useState('');
  const [hasError, setHasError] = useState(false);
  const [alreadySelectedError, setAlreadySelectedError] = useState(false);
  const actionData = watch('actionData');
  const idx = actionData
    .map((action) => action.type)
    .indexOf('Remove_Specific_Shares');
  const actionAtIndex = actionData[idx];
  if (!isRemoveSpecificSharesAction(actionAtIndex)) {
    return null;
  }

  const currentAction: RemoveSpecificSharesActionType = actionAtIndex;
  const error: FieldErrors<RemoveSpecificSharesActionType> | undefined =
    errors?.actionData?.[idx];

  function removeSelection(selectedShare: string) {
    const newList = currentAction.externalshare.filter(
      (share) => share !== selectedShare
    );
    setValue(`actionData.${idx}.externalshare`, [...newList]);
  }

  return (
    <Stack sx={{ p: 1 }}>
      <Stack>
        <Stack flexDirection={'row'} gap={1}>
          <Controller
            name={`actionData.${idx}.externalshare`}
            control={control}
            render={() => (
              <Box sx={{ width: 300 }}>
                <UsersAutocomplete
                  autocompleteValue={shareAddress}
                  setAutoCompleteValue={
                    setShareAddress as Dispatch<SetStateAction<string>>
                  }
                  errors={
                    hasError || !!error?.externalshare || alreadySelectedError
                  }
                  helperText={
                    hasError
                      ? 'Please provide a valid share address.'
                      : alreadySelectedError
                      ? 'This share address is already selected.'
                      : error?.externalshare
                      ? error?.externalshare?.message
                      : ' '
                  }
                  freeSolo={true}
                  onSelection={(value) => {
                    if (!value) return;
                    if (!emailRegex.test(value)) {
                      setHasError(true);
                    } else if (currentAction.externalshare?.includes(value)) {
                      setHasError(false);
                      setAlreadySelectedError(true);
                    } else {
                      setHasError(false);
                      setAlreadySelectedError(false);
                      setValue(
                        `actionData.${idx}.externalshare`,
                        currentAction.externalshare
                          ? [...currentAction.externalshare, value]
                          : [value]
                      );
                      setShareAddress('');
                      trigger(`actionData.${idx}.externalshare`);
                    }
                  }}
                />
              </Box>
            )}
          />
        </Stack>
        <Box sx={{ maxHeight: 200 }}>
          <Paper sx={{ minWidth: 470, overflow: 'hidden', maxHeight: 200 }}>
            <TableContainer sx={{ minWidth: 470, maxHeight: 200 }}>
              <Table size={'small'} stickyHeader aria-label="selected-shares">
                <TableHead>
                  <TableRow>
                    <TableCell>Shares to Remove</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {currentAction.externalshare
                    ? currentAction.externalshare.map((share) => (
                        <TableRow key={share}>
                          <TableCell>
                            <Stack
                              flexDirection="row"
                              alignItems="center"
                              justifyContent="space-between"
                            >
                              <Typography variant="body2">{share}</Typography>
                              <Tooltip arrow title="Delete">
                                <span>
                                  <IconButton
                                    onClick={() => {
                                      removeSelection(share);
                                    }}
                                  >
                                    <Delete />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      ))
                    : null}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Box>
      </Stack>
    </Stack>
  );
}
